import React, { useEffect, useState } from "react";

import "./AppointmentPopup.css";

import Swal from "sweetalert2";

import { API_BASE } from "../apiConfig";

export const emptyVitals = { bp: "", sugar: "", height: "", weight: "", temperature: "" };

export function AppointmentVitalsFields({ values = {}, onChange, disabled = false }) {
    const fields = [
        ["bp", "Blood Pressure (BP)", "e.g. 120/80 mmHg"],
        ["sugar", "Blood Sugar", "e.g. 110 mg/dL"],
        ["height", "Height (cm)", "e.g. 165"],
        ["weight", "Weight (kg)", "e.g. 65"],
        ["temperature", "Body Temperature (°F)", "e.g. 98.6"]
    ];
    return (
        <div className="appointment-vitals-grid" >
            {fields.map(([key, label, placeholder]) => (
                <label key={key} style={{ fontWeight: 600 }}>
                    {label}
                    <input
                        className="form-control"
                        name={key}
                        type="text"
                        maxLength={100}
                        placeholder={placeholder}
                        value={values[key] ?? ""}
                        disabled={disabled}
                        onChange={(event) => onChange(key, event.target.value)}
                        
                    />
                </label>
            ))}
        </div>
    );
}

export default function AppointmentPopup({

    patient,

    onClose,

    onSuccess

}) {

    /*

    |--------------------------------------------------------------------------

    | STATE

    |--------------------------------------------------------------------------

    */

    const [vitals, setVitals] = useState({ ...emptyVitals });

    const changeVital = (field, value) => {
        setVitals(previous => ({ ...previous, [field]: value }));
    };

    const [purpose, setPurpose] =

        useState("");

    const [priority, setPriority] =

        useState("normal");

    const [

        casualtyDetails,

        setCasualtyDetails

    ] = useState("");

    const [

        casualtyMedicine,

        setCasualtyMedicine

    ] = useState("");

    const [saving, setSaving] =

        useState(false);

    const [specialities, setSpecialities] = useState([]);

    const [speciality, setSpeciality] = useState("");

    const [doctors, setDoctors] = useState([]);

    const [doctorId, setDoctorId] = useState("");

    const [loadingSpecialities, setLoadingSpecialities] = useState(false);

    const [loadingDoctors, setLoadingDoctors] = useState(false);

    /*

    |--------------------------------------------------------------------------

    | LOAD SPECIALITIES

    |--------------------------------------------------------------------------

    */

    useEffect(() => {

        let cancelled = false;

        const loadSpecialities = async () => {

            try {

                setLoadingSpecialities(true);

                const response = await fetch(`${API_BASE}get_specialities.php`);

                const text = await response.text();

                if (!text.trim()) {

                    throw new Error("Speciality API returned an empty response.");

                }

                const result = JSON.parse(text);

                if (!response.ok || result.status !== "success") {

                    throw new Error(result.msg || "Unable to load specialities.");

                }

                if (!cancelled) {

                    setSpecialities(Array.isArray(result.data) ? result.data : []);

                }

            } catch (error) {

                console.error("Speciality Load Error:", error);

                if (!cancelled) {

                    setSpecialities([]);

                }

            } finally {

                if (!cancelled) {

                    setLoadingSpecialities(false);

                }

            }

        };

        loadSpecialities();

        return () => {

            cancelled = true;

        };

    }, []);

    /*

    |--------------------------------------------------------------------------

    | LOAD DOCTORS WHEN SPECIALITY CHANGES

    |--------------------------------------------------------------------------

    */

    useEffect(() => {

        let cancelled = false;

        setDoctorId("");

        setDoctors([]);

        if (!speciality) {

            return () => {

                cancelled = true;

            };

        }

        const loadDoctors = async () => {

            try {

                setLoadingDoctors(true);

                const response = await fetch(

                    `${API_BASE}get_doctors.php?speciality=${encodeURIComponent(speciality)}`

                );

                const text = await response.text();

                if (!text.trim()) {

                    throw new Error("Doctor API returned an empty response.");

                }

                const result = JSON.parse(text);

                if (!response.ok || result.status !== "success") {

                    throw new Error(result.msg || "Unable to load doctors.");

                }

                if (!cancelled) {

                    setDoctors(Array.isArray(result.data) ? result.data : []);

                }

            } catch (error) {

                console.error("Doctor Load Error:", error);

                if (!cancelled) {

                    setDoctors([]);

                }

            } finally {

                if (!cancelled) {

                    setLoadingDoctors(false);

                }

            }

        };

        loadDoctors();

        return () => {

            cancelled = true;

        };

    }, [speciality]);

    /*

    |--------------------------------------------------------------------------

    | GET PATIENT ID

    |--------------------------------------------------------------------------

    */

    const getPatientId = () => {

        if (!patient) {

            return 0;

        }

        const id =

            patient.id ??

            patient.patient_id ??

            patient.patientId ??

            patient.ID ??

            0;

        return Number(id);

    };

    /*

    |--------------------------------------------------------------------------

    | GET MR NUMBER

    |--------------------------------------------------------------------------

    */

    const getMrNo = () => {

        if (!patient) {

            return "-";

        }

        return (

            patient.mr_no ||

            patient.mrNo ||

            patient.MRNo ||

            patient.regNo ||

            "-"

        );

    };

    /*

    |--------------------------------------------------------------------------

    | SUBMIT APPOINTMENT

    |--------------------------------------------------------------------------

    */

    const handleSubmit = async () => {

        /*

        |--------------------------------------------------------------------------

        | PATIENT VALIDATION

        |--------------------------------------------------------------------------

        */

        if (!patient) {

            Swal.fire({

                icon: "error",

                title:

                    "Patient Missing",

                text:

                    "No patient was selected.",

                zIndex: 999999

            });

            return;

        }

        const patientId =

            getPatientId();

        if (

            !patientId ||

            patientId <= 0

        ) {

            Swal.fire({

                icon: "error",

                title:

                    "Invalid Patient ID",

                html: `

                    <div style="

                        text-align:left;

                        font-size:14px;

                    ">

                        <strong>

                            Patient ID was not found.

                        </strong>

                        <br/><br/>

                        Please check the patient API response.

                        <br/><br/>

                        <strong>

                            Received ID:

                        </strong>

                        ${patient.id ?? "undefined"}

                    </div>

                `,

                zIndex: 999999

            });

            return;

        }

        /*

        |--------------------------------------------------------------------------

        | DOCTOR ROUTING VALIDATION

        |--------------------------------------------------------------------------

        */

        if (!speciality) {

            Swal.fire({

                icon: "warning",

                title: "Speciality Required",

                text: "Please select a speciality.",

                zIndex: 999999

            });

            return;

        }

        if (!doctorId) {

            Swal.fire({

                icon: "warning",

                title: "Doctor Required",

                text: "Please select a doctor.",

                zIndex: 999999

            });

            return;

        }

        /*

        |--------------------------------------------------------------------------

        | PURPOSE VALIDATION

        |--------------------------------------------------------------------------

        */

        if (!purpose.trim()) {

            Swal.fire({

                icon: "warning",

                title:

                    "Purpose Required",

                text:

                    "Please enter the purpose of the visit.",

                zIndex: 999999

            });

            return;

        }

        /*

        |--------------------------------------------------------------------------

        | SAVE

        |--------------------------------------------------------------------------

        */

        try {

            setSaving(true);

            /*

            |--------------------------------------------------------------------------

            | FORM DATA

            |--------------------------------------------------------------------------

            */

            const data =

                new FormData();

            data.append(

                "patient_id",

                String(patientId)

            );

            data.append(

                "purpose",

                purpose.trim()

            );

            data.append(

                "priority",

                priority

            );

            data.append(

                "casualty_details",

                casualtyDetails.trim()

            );

            data.append(

                "casualty_medicine",

                casualtyMedicine.trim()

            );

            data.append(

                "speciality",

                speciality

            );

            ["bp", "sugar", "height", "weight", "temperature"].forEach((key) => {
                data.append(key, String(vitals[key] ?? "").trim());
            });

            data.append(

                "doctor_id",

                String(doctorId)

            );

            /*

            |--------------------------------------------------------------------------

            | IMPORTANT

            |--------------------------------------------------------------------------

            |

            | We intentionally DO NOT send appointment_date from React.

            |

            | PHP generates authoritative India time.

            |

            |--------------------------------------------------------------------------

            */

            /*

            |--------------------------------------------------------------------------

            | API

            |--------------------------------------------------------------------------

            */

            const response =

                await fetch(

                    `${API_BASE}add_appointment.php`,

                    {

                        method: "POST",

                        body: data

                    }

                );

            /*

            |--------------------------------------------------------------------------

            | RAW RESPONSE

            |--------------------------------------------------------------------------

            */

            const responseText =

                await response.text();

            console.log(

                "Appointment HTTP:",

                response.status

            );

            console.log(

                "Appointment Response:",

                responseText

            );

            /*

            |--------------------------------------------------------------------------

            | EMPTY RESPONSE

            |--------------------------------------------------------------------------

            */

            if (

                !responseText.trim()

            ) {

                throw new Error(

                    `Appointment API returned an empty response. HTTP Status: ${response.status}`

                );

            }

            /*

            |--------------------------------------------------------------------------

            | JSON

            |--------------------------------------------------------------------------

            */

            let result;

            try {

                result =

                    JSON.parse(

                        responseText

                    );

            } catch (jsonError) {

                console.error(

                    "Invalid appointment JSON:",

                    responseText

                );

                throw new Error(

                    "Appointment API returned invalid JSON."

                );

            }

            /*

            |--------------------------------------------------------------------------

            | ALREADY EXISTS

            |--------------------------------------------------------------------------

            */

            if (

                result.status ===

                "exists"

            ) {

                Swal.fire({

                    icon:

                        "warning",

                    title:

                        "Already in Queue",

                    text:

                        result.msg ||

                        "This patient is already in the doctor queue.",

                    confirmButtonText:

                        "OK",

                    zIndex:

                        999999

                });

                return;

            }

            /*

            |--------------------------------------------------------------------------

            | HTTP ERROR

            |--------------------------------------------------------------------------

            */

            if (!response.ok) {

                throw new Error(

                    result.msg ||

                    result.message ||

                    result.error ||

                    `HTTP Error ${response.status}`

                );

            }

            /*

            |--------------------------------------------------------------------------

            | SUCCESS

            |--------------------------------------------------------------------------

            */

            if (

                result.status ===

                "success"

            ) {

                /*

                |--------------------------------------------------------------------------

                | INDIA TIME RETURNED FROM PHP

                |--------------------------------------------------------------------------

                */

                const indiaTime =

                    result.appointment_date_india ||

                    "-";

                await Swal.fire({

                    icon:

                        "success",

                    title:

                        "Added to Doctor Queue",

                    html: `

                        <div style="

                            text-align:center;

                            line-height:1.7;

                        ">

                            <div>

                                ${priority ===

                            "emergency"

                            ? "Patient added as Emergency Priority."

                            : "Patient added to waiting list."

                        }

                            </div>

                            <div style="margin-top:10px;">

                                <strong>${result.speciality || speciality}</strong>

                                 • 

                                ${result.doctor?.name || "Selected Doctor"}

                            </div>

                            <div

                                style="

                                    margin-top:10px;

                                    font-weight:600;

                                "

                            >

                                <i

                                    class="fa fa-clock-o"

                                ></i>

                                 

                                ${indiaTime}

                            </div>

                        </div>

                    `,

                    timer:

                        2500,

                    showConfirmButton:

                        false,

                    zIndex:

                        999999

                });

                /*

                |--------------------------------------------------------------------------

                | REFRESH PARENT

                |--------------------------------------------------------------------------

                */

                if (onSuccess) {

                    await onSuccess(

                        result

                    );

                }

                /*

                |--------------------------------------------------------------------------

                | CLOSE

                |--------------------------------------------------------------------------

                */

                if (onClose) {

                    onClose();

                }

                return;

            }

            /*

            |--------------------------------------------------------------------------

            | OTHER ERROR

            |--------------------------------------------------------------------------

            */

            throw new Error(

                result.msg ||

                result.message ||

                "Unable to create appointment."

            );

        } catch (error) {

            console.error(

                "Appointment Error:",

                error

            );

            Swal.fire({

                icon:

                    "error",

                title:

                    "Appointment Error",

                html: `

                    <div

                        style="

                            text-align:left;

                            font-size:14px;

                            line-height:1.6;

                            word-break:break-word;

                        "

                    >

                        <strong>

                            Unable to add patient to doctor queue.

                        </strong>

                        <br/><br/>

                        <div>

                            <strong>

                                Error:

                            </strong>

                            ${error.message || "Unknown error"}

                        </div>

                        <br/>

                        <div>

                            <strong>

                                Patient ID:

                            </strong>

                            ${getPatientId() || "-"}

                        </div>

                        <div>

                            <strong>

                                Patient:

                            </strong>

                            ${patient?.name || "-"}

                        </div>

                        <div>

                            <strong>

                                MR No:

                            </strong>

                            ${getMrNo()}

                        </div>

                    </div>

                `,

                confirmButtonText:

                    "OK",

                confirmButtonColor:

                    "#dc3545",

                zIndex:

                    999999

            });

        } finally {

            setSaving(false);

        }

    };

    /*

    |--------------------------------------------------------------------------

    | CLOSE

    |--------------------------------------------------------------------------

    */

    const handleClose = () => {

        if (saving) {

            return;

        }

        if (onClose) {

            onClose();

        }

    };

    /*

    |--------------------------------------------------------------------------

    | NO PATIENT

    |--------------------------------------------------------------------------

    */

    if (!patient) {

        return null;

    }

    /*

    |--------------------------------------------------------------------------

    | RENDER

    |--------------------------------------------------------------------------

    */

    return (
        <div className="appointment-overlay" role="presentation">
            <div className="appointment-modal" role="dialog" aria-modal="true" aria-labelledby="appointment-title">
                <header className="appointment-header">
                    <div className="appointment-header-heading">
                        <span className="appointment-header-symbol"><i className="fa fa-stethoscope" /></span>
                        <div>
                            <h3 id="appointment-title">Create Appointment</h3>
                            <span>Add patient to doctor consultation queue</span>
                        </div>
                    </div>
                    <button type="button" className="appointment-close" onClick={handleClose} disabled={saving} aria-label="Close appointment">
                        <i className="fa fa-times" />
                    </button>
                </header>

                <div className="appointment-body">
                    <div className="appointment-patient">
                        <div className="appointment-avatar"><i className="fa fa-user" /></div>
                        <div className="appointment-patient-info">
                            <strong>{patient.name || "-"}</strong>
                            <div className="appointment-patient-meta">
                                <span><i className="fa fa-id-card-o" /> MR ID: {getMrNo()}</span>
                                <span><i className="fa fa-phone" /> Phone: {patient.phone || "-"}</span>
                            </div>
                        </div>
                    </div>

                    <div className="appointment-two-column">
                        <section className="appointment-panel appointment-main-panel" aria-labelledby="appointment-details-title">
                            <div className="appointment-section-heading">
                                <i className="fa fa-user-md" />
                                <div><h4 id="appointment-details-title">Consultation Details</h4><small>Select doctor and describe the reason for visit</small></div>
                            </div>
                            <div className="appointment-form-grid">
                                <div className="appointment-field">
                                    <label htmlFor="appointment-speciality">Speciality <span>*</span></label>
                                    <select
                                        id="appointment-speciality"
                                        className="form-control"
                                        value={speciality}
                                        onChange={(e) => setSpeciality(e.target.value)}
                                        disabled={saving || loadingSpecialities}
                                    >
                                        <option value="">{loadingSpecialities ? "Loading specialities..." : "Select Speciality"}</option>
                                        {specialities.map((item) => <option key={item} value={item}>{item}</option>)}
                                    </select>
                                </div>
                                <div className="appointment-field">
                                    <label htmlFor="appointment-doctor">Doctor Name <span>*</span></label>
                                    <select
                                        id="appointment-doctor"
                                        className="form-control"
                                        value={doctorId}
                                        onChange={(e) => setDoctorId(e.target.value)}
                                        disabled={saving || !speciality || loadingDoctors}
                                    >
                                        <option value="">
                                            {!speciality ? "Select Speciality First" : loadingDoctors ? "Loading doctors..." : doctors.length === 0 ? "No doctors available" : "Select Doctor"}
                                        </option>
                                        {doctors.map((doctor) => <option key={doctor.id} value={doctor.id}>{doctor.name}</option>)}
                                    </select>
                                </div>
                                <div className="appointment-field appointment-field-wide">
                                    <label htmlFor="appointment-purpose">Purpose of Visit <span>*</span></label>
                                    <textarea
                                        id="appointment-purpose"
                                        value={purpose}
                                        onChange={(e) => setPurpose(e.target.value)}
                                        placeholder="Why is the patient coming for consultation?"
                                        disabled={saving}
                                        rows={5}
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="appointment-panel appointment-vitals-panel" aria-labelledby="appointment-vitals-title">
                            <div className="appointment-section-heading">
                                <i className="fa fa-heartbeat" />
                                <div><h4 id="appointment-vitals-title">Vital Signs</h4><small>Enter available measurements (optional)</small></div>
                            </div>
                            <AppointmentVitalsFields values={vitals} onChange={changeVital} disabled={saving} />
                        </section>
                    </div>

                    <section className="appointment-priority-panel" aria-labelledby="appointment-priority-title">
                        <div className="appointment-section-heading appointment-priority-heading">
                            <i className="fa fa-flag" />
                            <div><h4 id="appointment-priority-title">Appointment Priority</h4><small>Choose the appropriate queue priority</small></div>
                        </div>
                        <div className="priority-options">
                            <label className={`priority-option normal ${priority === "normal" ? "selected" : ""}`}>
                                <input type="radio" name="priority" value="normal" checked={priority === "normal"} onChange={() => setPriority("normal")} disabled={saving} />
                                <i className="fa fa-clock-o" /><span>Normal</span>
                            </label>
                            <label className={`priority-option emergency ${priority === "emergency" ? "selected" : ""}`}>
                                <input type="radio" name="priority" value="emergency" checked={priority === "emergency"} onChange={() => setPriority("emergency")} disabled={saving} />
                                <i className="fa fa-exclamation-triangle" /><span>Emergency</span>
                            </label>
                        </div>
                    </section>

                    {priority === "emergency" && (
                        <section className="emergency-box" aria-labelledby="appointment-emergency-title">
                            <div className="emergency-title" id="appointment-emergency-title">
                                <i className="fa fa-exclamation-triangle" /> Emergency / Casualty Details
                            </div>
                            <div className="appointment-emergency-grid">
                                <div className="appointment-field">
                                    <label htmlFor="appointment-casualty">Casualty Details</label>
                                    <textarea id="appointment-casualty" value={casualtyDetails} onChange={(e) => setCasualtyDetails(e.target.value)} placeholder="Enter details from casualty..." disabled={saving} rows={3} />
                                </div>
                                <div className="appointment-field">
                                    <label htmlFor="appointment-casualty-medicine">Medicine Given</label>
                                    <textarea id="appointment-casualty-medicine" value={casualtyMedicine} onChange={(e) => setCasualtyMedicine(e.target.value)} placeholder="Enter medicine given in casualty..." disabled={saving} rows={3} />
                                </div>
                            </div>
                        </section>
                    )}
                </div>

                <footer className="appointment-footer">
                    <span className="appointment-footer-note"><i className="fa fa-info-circle" /> Fields marked * are required</span>
                    <div className="appointment-footer-actions">
                        <button type="button" className="appointment-cancel" onClick={handleClose} disabled={saving}>Cancel</button>
                        <button type="button" className={`appointment-submit ${priority === "emergency" ? "emergency-submit" : ""}`} onClick={handleSubmit} disabled={saving}>
                            <i className={saving ? "fa fa-spinner fa-spin" : "fa fa-check"} />{" "}
                            {saving ? "Adding..." : priority === "emergency" ? "Add Emergency" : "Add to Doctor Queue"}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}
